import { queryOptions, useQuery } from '@tanstack/react-query';

import { POLLING_DELAY_MS } from '@cast/constants';
import { CostReportClusterSummary } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';
import { CostReportClusterSummaryNormalized } from './cost-report-normalized';
import { enhanceClusterSummary, toBigNumberTypes } from './cost-report';

const ORGANIZATION_CLUSTERS_SUMMARY_QUERY_KEY = 'organization-clusters-summary';

export const getClustersSummaryQueryKey = (
  tourStepKey: string | null | undefined
) => [ORGANIZATION_CLUSTERS_SUMMARY_QUERY_KEY, tourStepKey] as const;

export const getClustersSummaryQueryOptions = (
  tourStepKey: string | null | undefined,
  canViewCluster: (clusterId: string) => boolean
) =>
  queryOptions({
    queryKey: getClustersSummaryQueryKey(tourStepKey),
    queryFn: async () =>
      apiClient.costReport.getOrganizationClustersSummary().then(
        (resp) =>
          resp.data.items
            ?.filter((d) => d.clusterId && canViewCluster(d.clusterId))
            ?.map((item) => {
              const normalizedSummary = toBigNumberTypes<
                CostReportClusterSummary,
                CostReportClusterSummaryNormalized
              >(item, ['clusterId', 'clusterScore']);
              return enhanceClusterSummary(normalizedSummary);
            }) || null
      ),
    refetchInterval: POLLING_DELAY_MS,
  });

export const useClustersSummary = (
  tourStepKey: string | null | undefined,
  canViewCluster: (clusterId: string) => boolean
) => {
  const {
    data: summaries,
    isLoading,
    error,
    refetch,
  } = useQuery(getClustersSummaryQueryOptions(tourStepKey, canViewCluster));

  return { summaries: summaries || undefined, isLoading, error, refetch };
};
